import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getAssistantById, getAssistantWorkspacesByAssistantId } from "@/db/assistants"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.assistant', supabase)

type Row = Tables<'assistants'>
type WsRow = Tables<'assistant_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const assistantWorkspaces = await getAssistantWorkspacesByAssistantId(supabase, id)
	return assistantWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 *
 * Here we ignore any changes from these table:
 * - assistant_collections
 * - assistant_tools
 * - assistant_files
 * since they don't use state and are manually loaded via fetchDataFunctions
 *
 * @param workspaceId
 * @param setAssistant
 * @returns
 */
export function makeAssistantChangeHandler(
	setAssistant: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `assistant-changes-${uuid}`,
		channelName: `assistant-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'assistants',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const assistant = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setAssistant(prev => {
					const exists = prev.some(p => p.id === assistant.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling assistant update:${printObject(assistant)}`)
					return prev.map(p => p.id === assistant.id ? assistant : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setAssistant
 * @returns
 */
export function makeAssistantWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setAssistant: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `assistant-ws-changes-${uuid}`,
		channelName: `assistant-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'assistant_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const assistantWs = payload.new as WsRow
				const id = assistantWs.assistant_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const assistant = await getAssistantById(supabase, id)
					if (assistant) {
						setAssistant(prev => {
 							if (prev.some(p => p.id === assistant.id)) return prev
							logger.info(`Handling assistant Insert:${printObject(assistant)}`)
 							return [...prev, assistant]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const assistantWs = payload.old as WsRow
				const id = assistantWs.assistant_id
				setAssistant(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling assistant Delete:${id}`)
					return prev.filter((prevItem: any) => prevItem.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}